// src/services/userservice.js

// Obtener la URL base de la API desde las variables de entorno
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_GROUPS_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_USER_LISTING_URL = `${process.env.REACT_APP_API_URL || ''}/api`;

/**
 * Obtener listado de usuarios desde el backend
 */
export const getUsersListing = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/getUsersListing`);
    if (!response.ok) {
      throw new Error(`Error al obtener usuarios: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error en getUsersListing:', error);
    return [];
  }
};

/**
 * Obtener listado de usuarios desde el nuevo endpoint (port 30109)
 * Endpoint: https://datco-test.ddns.net:30109/api/User/GetUserListing
 */
export const getUserListing = async () => {
  try {
    const response = await fetch(`${API_USER_LISTING_URL}/User/GetUserListing`);
    if (!response.ok) {
      throw new Error(`Error al obtener usuarios: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error en getUserListing:', error);
    throw error;
  }
};

/**
 * Agregar nuevo usuario
 * @param {Object} userData - { name, lastname, email, username, group_ID }
 */
export const addUserAPI = async (userData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/addUser`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al agregar usuario: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en addUserAPI:', error);
    throw error;
  }
};

/**
 * Setear nueva contraseña para un usuario
 * @param {number} userID 
 * @param {string} plainPassword 
 */
export const setUserPassword = async (userID, plainPassword) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/setPasswordUser?userID=${userID}&plainPassword=${encodeURIComponent(plainPassword)}`,
      {
        method: 'POST',
        headers: {
          Accept: '*/*',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al establecer contraseña: ${response.status} - ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error('Error en setUserPassword:', error);
    throw error;
  }
};

/**
 * Obtener listado de grupos de usuario (desde API base distinta)
 */
export const getUsersGroupsListing = async () => {
  try {
    const response = await fetch(`${API_GROUPS_BASE_URL}/getUsersGroupsListing`);
    if (!response.ok) {
      throw new Error(`Error al obtener grupos: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error en getUsersGroupsListing:', error);
    return [];
  }
};

/**
 * Obtener información completa de un usuario
 * Endpoint: GET /api/User/GetUserInfo?userName={username}
 * @param {string} username - Nombre de usuario
 * @returns {Promise<Object>} Información completa del usuario
 */
export const getUserInfo = async (username) => {
  try {
    const response = await fetch(
      `${API_USER_LISTING_URL}/User/GetUserInfo?userName=${encodeURIComponent(username)}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al obtener información del usuario: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en getUserInfo:', error);
    throw error;
  }
};

/**
 * Editar datos de un usuario
 * Endpoint: POST /api/User/EditUserData
 * @param {Object} userData - Datos del usuario a editar
 * @param {number} userData.id - ID del usuario
 * @param {string} userData.name - Nombre
 * @param {string} userData.lastName - Apellido
 * @param {string} userData.username - Username
 * @param {string} userData.email - Email
 * @param {number} userData.groupID - ID del grupo
 * @param {number} userData.ceID - ID del CE
 * @param {Array<number>} userData.modules - Array de IDs de módulos
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const editUserData = async (userData) => {
  try {
    const response = await fetch(`${API_USER_LISTING_URL}/User/EditUserData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al editar usuario: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en editUserData:', error);
    throw error;
  }
};

/**
 * Cambiar estado de un usuario (bloquear/desbloquear/activar/desactivar)
 * Endpoint: POST /api/User/EditUserStatus
 * @param {string} username - Username del usuario
 * @param {number} userStatus - Estado: 0=INACTIVO, 1=ACTIVO, 2=BLOQUEADO
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const editUserStatus = async (username, userStatus) => {
  try {
    const response = await fetch(`${API_USER_LISTING_URL}/User/EditUserStatus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        username,
        userStatus,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al cambiar estado del usuario: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en editUserStatus:', error);
    throw error;
  }
};

/**
 * Actualizar contraseña de un usuario
 * Endpoint: POST /api/User/UpdatePass
 * @param {string} username - Username del usuario
 * @param {string} password - Nueva contraseña
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const updatePassword = async (username, password) => {
  try {
    const response = await fetch(`${API_USER_LISTING_URL}/User/UpdatePass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al actualizar contraseña: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en updatePassword:', error);
    throw error;
  }
};

/**
 * Activar/desactivar flag de reseteo de contraseña obligatorio
 * Endpoint: POST /api/User/EditUserResetPasswordFlag
 * @param {string} username - Username del usuario
 * @param {number} resetFlagPassword - 0 = No requiere cambio, 1 = Requiere cambio obligatorio
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const editUserResetPasswordFlag = async (username, resetFlagPassword) => {
  try {
    const response = await fetch(`${API_USER_LISTING_URL}/User/EditUserResetPasswordFlag`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        username,
        resetFlagPassword,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al actualizar flag de reseteo: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en editUserResetPasswordFlag:', error);
    throw error;
  }
};

/**
 * Agregar nuevo usuario
 * Endpoint: POST /api/User/AddUser
 * @param {Object} userData - Datos del nuevo usuario
 * @param {string} userData.name - Nombre
 * @param {string} userData.lastName - Apellido
 * @param {string} userData.username - Username
 * @param {string} userData.email - Email
 * @param {number} userData.groupID - ID del grupo
 * @param {number} userData.ceID - ID del CE
 * @param {Array<Object>} userData.modules - Array de módulos [{id: number, name: string}]
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const addUser = async (userData) => {
  try {
    const response = await fetch(`${API_USER_LISTING_URL}/User/AddUser`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al agregar usuario: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en addUser:', error);
    throw error;
  }
};

/**
 * Obtener listado de scoring por estado
 * Endpoint:
 * /api/Scoring/GetScoringListing?scoringStatus=INCOMPLETOS
 * Estados posibles: 'DISPONIBLES', 'INCOMPLETOS', 'RECHAZADOS'
 */
export const getScoringListing = async (scoringStatus, sellerID) => {
  try {
    
    const response = await fetch(
      `${API_USER_LISTING_URL}/Scoring/GetScoringListing?scoringStatus=${encodeURIComponent(scoringStatus)}&sellerID=${encodeURIComponent(sellerID)}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );


    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error en respuesta:', errorText);
      throw new Error(
        `Error al obtener scoring: ${response.status} - ${errorText}`
      );
    }

    const data = await response.json();
    
    // Verificar que sea un array
    if (!Array.isArray(data)) {
      console.warn('La respuesta no es un array:', data);
      return [];
    }
    
    return data;
  } catch (error) {
    console.error('Error en getScoringListing:', error);
    throw error;
  }
};

/**
 * GetInfoAvailableScoring
 */
export const getInfoAvailableScoring = async (group, order) => {
    try {
        
        const response = await fetch(
            `${API_USER_LISTING_URL}/Scoring/GetInfoAvailableScoring?group=${group}&order=${order}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            }
        );


        if (!response.ok) {
            const errorText = await response.text();
            console.error('Error en respuesta:', errorText);
            throw new Error(`Error ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error en getInfoAvailableScoring:', error);
        throw error;
    }
};

/**
 * Reset Case - Deshacer un caso
 * Endpoint: POST /api/Scoring/resetCase
 * @param {string} group - Grupo del caso
 * @param {string} order - Orden del caso
 * @returns {Promise<Object>} Respuesta del servidor
 */
export const resetCase = async (group, order) => {
  try {
    const response = await fetch(`${API_USER_LISTING_URL}/Scoring/resetCase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        group,
        order,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al resetear caso: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en resetCase:', error);
    throw error;
  }
};

/**
 * Obtener tracking de eventos de un scoring
 * Endpoint: GET /api/Scoring/GetScoringTracking?group={group}&order={order}
 * @param {string} group - Grupo del caso
 * @param {string} order - Orden del caso
 * @returns {Promise<Array>} Lista de eventos del tracking
 */
export const getScoringTracking = async (group, order) => {
  try {
    const response = await fetch(
      `${API_USER_LISTING_URL}/Scoring/GetScoringTracking?group=${encodeURIComponent(group)}&order=${encodeURIComponent(order)}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error al obtener tracking: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    // Verificar que sea un array
    if (!Array.isArray(data)) {
      console.warn('La respuesta no es un array:', data);
      return [];
    }
    
    return data;
  } catch (error) {
    console.error('Error en getScoringTracking:', error);
    throw error;
  }
};
